<template>
  <div>
    <b-form @submit.prevent="login">
      <template v-if="step === 1">
        <div class="form-group">
          <label for="number">Mobile Number</label>
          <input
            v-model="formattedPhoneNumber"
            @input="formatPhoneNumber"
            id="number"
            type="tel"
            class="phone-input form-control"
            required
            minlength="10"
            maxlength="14"
            @keypress="onlyNumber"
            placeholder="Enter your phone number"
            :disabled="this.email != ''"
          >
        </div>
        <div class="text-center">OR</div>
        <form @submit.prevent="nextStep">
          <div class="form-group">
            <label for="email">Email</label>
            <input v-model="email" id="email" type="email" class="email-input form-control" placeholder="Enter your email" :disabled="this.phone != ''">
          </div>
          <button type="submit" :disabled="!allowLogin" class="btn btn-primary w-100" :class="(errorMsg ? 'mb-3 mb-md-3' : 'mb-4 mb-md-5')">
            <div class="spinner-border spinner-border-sm" v-if="signingIn"></div>
            <span v-else>Next</span>
          </button>
          <span class="invalid-feedback d-block mb-3 mt-0 text-center" v-if="errorMsg"> {{ errorMsg }}</span>
        </form>
      </template>
      <div v-if="step === 2">
        <auth-with-phone v-if="formattedPhoneNumber && formattedPhoneNumber.length > 0" :phone.sync="formattedPhoneNumber" :code.sync="code" @resetStep="resetStep" />
        <auth-with-email v-else :email.sync="email" :password.sync="password" @resetStep="resetStep" />
        <button type="submit" class="btn btn-primary w-100 mb-2" :disabled="(this.password == '' && this.email == '') && (this.phone == '' && this.code == '') || signingIn">
          <div class="spinner-border spinner-border-sm" v-if="signingIn"></div>
          <span v-else>Sign In</span>
        </button>
        <div class="custom-control custom-checkbox my-4">
          <input id="cbRememberMe" type="checkbox" class="custom-control-input" v-model="loginKeep">
          <label class="custom-control-label" for="cbRememberMe">
            Keep me signed in
          </label>
        </div>
      </div>
      <div v-if="step === 3">
        <form @submit.prevent="submitMfa">
          <div class="form-group">
            <label for="mfa_code">Authenticator Code</label>
            <input v-model="mfaCode" id="mfa_code" type="text" class="mfa-input form-control">
            <a href="/" class="float-right" @click.prevent="showMfaOptions = true">Can't sign in?</a>
          </div>
          <div v-if="showMfaOptions" class="form-group">
            <label for="mfa_backup_code">Enter a Backup Code</label>
            <input v-model="mfaBackupCode" id="mfa_backup_code" type="text" class="mfa-input form-control">
            <div v-if="showMfaContact">Or else contact us at 888-580-5593 ext 4</div>
          </div>
          <button type="submit" class="btn btn-primary w-100">Verify</button>
        </form>
      </div>
      <template v-if="!simpleLayout">
        <hr>
        <p class="font-weight-normal">New to {{ $store.state.settings.businessName }}?</p>
        <router-link to="/register" class="btn btn-outline-primary w-100">
          Create a new account
        </router-link>
      </template>
      <template v-else-if="loginFromCart && step === 1">
        <div class="d-flex w-100 align-items-center justify-content-center" :class="minimizeMargins ? 'mb-0' : 'mb-4'">
          <a @click="register" class="text-primary font-weight-bold text-uppercase cursor-pointer text-decoration-none">
            Create a new account
          </a>
        </div>
      </template>
    </b-form>
  </div>
</template>

<script>
  import AuthApiService from '@/api-services/auth.service';
  import AuthController from '@/controllers/auth.controller';

  export default {
    name: 'LoginForm',
    props: {
      simpleLayout: {
        type: Boolean,
        default: false
      },
      loginFromCart: {
        type: Boolean,
        default: false
      },
      minimizeMargins: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        step: 1,
        phone: '',
        formattedPhoneNumber: '',
        email: '',
        password: '',
        code: '',
        loginKeep: false,
        signingIn: false,
        isMaster: false,
        errorMsg: '',
        mfaToken: null,
        mfaCode: '',
        showMfaOptions: false,
        showMfaContact: false,
        mfaBackupCode: '',
        skipReload: false,
      };
    },
    computed: {
      allowLogin() {
        return this.checkEmailAndPhone();
      }
    },
    methods: {
      checkEmailAndPhone() {
        if (this.phone) {
          let phone = this.phone;
          if(phone.startsWith('+1')) {
            phone = phone.substring(2);
          }
          phone = phone.replace(/[^0-9]/g, '');
          if (phone && phone.length === 10) {
              return true;
          } else {
            this.errorMsg = 'Please enter a valid phone number.';
            return false;
          }
        } else if (this.email) {
          var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (this.email && this.email.length >= 6 && emailRegex.test(this.email)) {
            return true;
          } else {
            this.errorMsg = 'Please enter a valid email address.';
            return false;
          }
        }
        return false;
      },
      resetStep(){
        if(!this.email && !this.phone) {
          this.step = 1;
        }
      },
      async nextStep() {
        try {
          let phone = this.phone;
          if(phone.startsWith('+1')) {
            this.phone = phone.substring(2);
          }
          this.phone = this.phone.replace(/[^0-9]/g, '');
          if (this.phone && this.phone.length === 10) {
            this.signingIn = true;
            const response = await AuthApiService.verifyEmailOrPhone({telephone: this.phone});
            if (response) {
              AuthApiService.sendOTP(this.phone);
              this.signingIn = false;
              this.step = 2;
            }
          } else if (this.email) {
            this.signingIn = true;
            const response = await AuthApiService.verifyEmailOrPhone({email: this.email});
            if (response) {
              if (response.data.master_login) {
                localStorage.setItem('master-login', 'true');
              }
              this.signingIn = false;
              this.step = 2;
            }
          }
        } catch (e) {
          this.signingIn = false;
          if(!this.simpleLayout)
            this.$router.push({name: 'register', params: { email: this.email, phone: this.phone }}).catch(err => console.log(err));
          else {
            this.signingIn = false;
             this.$swal( e && e.response && e.response.data && e.response.data.message ? e.response.data.message : 'Unsuccessful login. Please try again.', '', 'error');
          }
        }
      },
      async login() {
        if ( this.phone && this.phone.replace(/[^0-9]/g, '').length ) {
          if ( !this.code || !this.code.length ) {
            //this.$swal('Error', 'Please enter your verification code.', 'error');
            this.alert.show = true;
            this.alert.message = 'Please enter your verification code.';
            return;
          }
        }

        this.signingIn = true;
        this.skipReload = false;
        await AuthApiService.login(
          {phone: this.phone.replace(/[^0-9]/g, ''), email: this.email},
          {code: this.code, password: this.password},
          this.$store.state.device_id
        ).then(response => {
          if ( response.data.mfa === true ) {
            this.step = 3;
            this.mfaToken = response.data.token;
            this.showMfaContact = response.data.showContact;
            this.skipReload = true;
            return;
          }

          this.handleLoginResponse(response);
        }).catch(error => {
          this.signingIn = false;
          this.$swal(error.response.data.message, '', 'error');
          if(!this.loginFromCart) {
            window.location.reload();
          }
        });
        if(this.loginFromCart){
          localStorage.setItem('goToStep', 2);
          if(localStorage.getItem('cartStep')){
            localStorage.removeItem("cartStep");
          }
        }

        if ( !this.skipReload ) {
          window.location.reload();
        }
      },
      handleLoginResponse(response) {
        if(response.data.status === 'success') {
          this.signingIn = false;
          response.data.loginKeep = this.loginKeep;
          AuthController.login(response.data);
          this.$store.dispatch("fetchCartItemsDetails");
          if (response.data.is_admin) {
            if (this.$root.$children[0].initWizard) {
              this.$root.$children[0].$refs.wizardModal.startWizard();
            } else {
              let redirect = 'admin-orders';
              if ( this.$store.state.isBasicPlan ) {
                redirect = 'admin-settings-about-us';
              }
              const redirectIntent = localStorage.getItem("redirectIntent");
              if (redirectIntent) {
                const { url } = JSON.parse(redirectIntent);
                this.skipReload = true;
                setTimeout(() => {
                  localStorage.removeItem("redirectIntent");
                  window.location.href = url;
                }, 200);
              }
              this.$router.push({name: redirect}).catch(err => console.log(err));
            }
          } else {
            if(localStorage.getItem('pathToLoadAfterLogin')) {
              let redirect = localStorage.getItem('pathToLoadAfterLogin');
              localStorage.removeItem('pathToLoadAfterLogin');
              this.$router.push(redirect);
            } else {
              this.$router.push({ name: 'cart' }).catch(err => console.log(err));
            }
            setTimeout(function (){
              window.location.reload();
            }, 1000);
          }
        } else if(response.data.status === 'error') {
          this.signingIn = false;
          this.$swal(response.data.message, '', 'error');
        }
      },
      async submitMfa() {
        let res;
        if ( this.mfaBackupCode.length ) {
          res = await AuthApiService.mfaVerify(this.mfaBackupCode, this.mfaToken, true);
        } else {
          res = await AuthApiService.mfaVerify(this.mfaCode, this.mfaToken, false);
        }

        this.handleLoginResponse(res);
      },
      register(){
        this.$router.push({ name: 'register' }).catch(err => console.log(err));
        localStorage.setItem('redirectToCartAfterlogin', true);
      },
      onlyNumber ($event) {
        //console.log($event.keyCode); //keyCodes value
        let keyCode = ($event.keyCode ? $event.keyCode : $event.which);
        if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) { // 46 is dot
            $event.preventDefault();
        }
      },
      formatPhoneNumber() {
        let input = this.formattedPhoneNumber.replace(/\D/g, ''); // Remove all non-digit characters

        if (input.length <= 3 && input.length > 0) {
          input = input.replace(/^(\d{0,3})/, '($1)');
        } else if (input.length <= 6 && input.length > 0) {
          input = input.replace(/^(\d{0,3})(\d{0,3})/, '($1) $2');
        } else if (input.length <= 10 && input.length > 0) {
          input = input.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
        }
        this.formattedPhoneNumber = input; // Set the formatted value to the model
        let cleanedValue = input.replace(/\D/g, ''); // Remove all non-digit characters
        this.phone = cleanedValue; // Set the cleaned value to the model
      }
    },
    watch: {
      phone() {
        this.errorMsg = '';
        this.resetStep();
      },
      email() {
        this.errorMsg = '';
        this.resetStep();
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/auth.scss';
</style>
